import React, { useRef, useState, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Camera, Upload, CheckCircle2, AlertTriangle, RotateCcw, Loader2 } from 'lucide-react'

interface ScanResult {
  readable: boolean
  size: string | null
  width: number | null
  profile: number | null
  rim: number | null
  load_index: string | null
  speed_rating: string | null
  brand: string | null
  confidence: 'high' | 'medium' | 'low'
  notes: string
}

interface TyreSizeScannerProps {
  onDetected: (size: string) => void
}

const MAX_EDGE = 1280 // longest edge in px after resize, keeps payloads small + fast
const JPEG_QUALITY = 0.85

// Downscale the photo client-side before it ever leaves the device - phone
// photos can be 8-12MB, we only need enough resolution to read printed text.
function resizeImageFile(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onerror = () => reject(new Error('Could not read that file'))
    reader.onload = () => {
      const img = new Image()
      img.onerror = () => reject(new Error('Could not read that image'))
      img.onload = () => {
        let { width, height } = img
        if (width > height && width > MAX_EDGE) {
          height = Math.round((height * MAX_EDGE) / width)
          width = MAX_EDGE
        } else if (height > MAX_EDGE) {
          width = Math.round((width * MAX_EDGE) / height)
          height = MAX_EDGE
        }
        const canvas = document.createElement('canvas')
        canvas.width = width
        canvas.height = height
        const ctx = canvas.getContext('2d')
        if (!ctx) return reject(new Error('Canvas not supported'))
        ctx.drawImage(img, 0, 0, width, height)
        resolve(canvas.toDataURL('image/jpeg', JPEG_QUALITY))
      }
      img.src = reader.result as string
    }
    reader.readAsDataURL(file)
  })
}

const confidenceStyles: Record<ScanResult['confidence'], string> = {
  high: 'bg-green-100 text-green-800 border-green-300',
  medium: 'bg-amber-100 text-amber-800 border-amber-300',
  low: 'bg-red-100 text-red-700 border-red-300'
}

export const TyreSizeScanner: React.FC<TyreSizeScannerProps> = ({ onDetected }) => {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [preview, setPreview] = useState<string | null>(null)
  const [scanning, setScanning] = useState(false)
  const [result, setResult] = useState<ScanResult | null>(null)
  const [editedSize, setEditedSize] = useState('')
  const [error, setError] = useState<string | null>(null)

  const reset = useCallback(() => {
    setPreview(null)
    setResult(null)
    setError(null)
    setEditedSize('')
    if (fileInputRef.current) fileInputRef.current.value = ''
  }, [])

  const handleFile = useCallback(async (file: File | null) => {
    if (!file) return
    setError(null)
    setResult(null)

    if (!file.type.startsWith('image/')) {
      setError('Please choose an image file.')
      return
    }

    try {
      const dataUrl = await resizeImageFile(file)
      setPreview(dataUrl)
      setScanning(true)

      const res = await fetch('/api/vision/tyre-size', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ image: dataUrl })
      })

      const json = await res.json()

      if (!res.ok) {
        setError(json.error || 'Could not analyse that photo. Please try again.')
        setScanning(false)
        return
      }

      const scanResult: ScanResult = json
      setResult(scanResult)
      setEditedSize(scanResult.size || '')
      setScanning(false)
    } catch (err) {
      console.error('Tyre scan failed:', err)
      setError('Something went wrong analysing that photo. Please try again, or type the size manually.')
      setScanning(false)
    }
  }, [])

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Scan Your Tyre Size</h2>
        <p className="text-gray-600">
          Take a clear, well-lit photo of your tyre's sidewall - we'll read the size printed on it.
        </p>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={(e) => handleFile(e.target.files?.[0] || null)}
      />

      <AnimatePresence mode="wait">
        {!preview ? (
          <motion.div
            key="picker"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="border-2 border-dashed border-gray-300 rounded-2xl p-10 text-center bg-gray-50"
          >
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Camera className="w-8 h-8 text-blue-600" />
            </div>
            <p className="text-gray-700 font-medium mb-1">Photo of the tyre sidewall</p>
            <p className="text-sm text-gray-500 mb-6">
              Fill the frame with the size text, e.g. "205/55R16" - avoid glare and shadows.
            </p>
            <button
              onClick={() => fileInputRef.current?.click()}
              className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 py-3 rounded-xl font-semibold hover:from-blue-700 hover:to-indigo-700 transition-all shadow-lg shadow-blue-600/25"
            >
              <Upload className="w-5 h-5" />
              Take or Upload Photo
            </button>
          </motion.div>
        ) : (
          <motion.div
            key="result"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white border border-gray-200 rounded-2xl p-6"
          >
            <div className="flex flex-col sm:flex-row gap-6">
              <img
                src={preview}
                alt="Tyre sidewall"
                className="w-full sm:w-40 h-40 object-cover rounded-xl border border-gray-200 flex-shrink-0"
              />

              <div className="flex-1 min-w-0">
                {scanning && (
                  <div className="flex items-center gap-2 text-gray-600">
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span>Reading tyre size...</span>
                  </div>
                )}

                {!scanning && error && (
                  <div className="flex items-start gap-2 text-red-700">
                    <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                    <p className="text-sm">{error}</p>
                  </div>
                )}

                {!scanning && !error && result && (
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      {result.readable ? (
                        <CheckCircle2 className="w-5 h-5 text-green-600" />
                      ) : (
                        <AlertTriangle className="w-5 h-5 text-amber-600" />
                      )}
                      <span
                        className={`text-xs font-semibold px-2 py-1 rounded-full border ${confidenceStyles[result.confidence]}`}
                      >
                        {result.confidence} confidence
                      </span>
                    </div>

                    {result.readable ? (
                      <>
                        <p className="text-sm text-gray-500 mb-1">Detected size</p>
                        <input
                          type="text"
                          value={editedSize}
                          onChange={(e) => setEditedSize(e.target.value)}
                          className="w-full text-2xl font-bold text-gray-900 font-mono bg-blue-50 border-2 border-blue-200 rounded-xl px-4 py-3 mb-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                        {(result.load_index || result.speed_rating) && (
                          <p className="text-sm text-gray-500 mb-2">
                            {result.load_index && `Load index ${result.load_index}`}
                            {result.load_index && result.speed_rating && ' · '}
                            {result.speed_rating && `Speed rating ${result.speed_rating}`}
                          </p>
                        )}
                        <p className="text-xs text-gray-400 mb-4">
                          AI reading - please check this matches what's printed on your tyre before ordering.
                        </p>
                        <div className="flex flex-wrap gap-3">
                          <button
                            onClick={() => editedSize && onDetected(editedSize)}
                            disabled={!editedSize}
                            className="flex-1 sm:flex-none bg-blue-600 text-white px-6 py-3 rounded-xl font-semibold hover:bg-blue-700 transition-colors disabled:opacity-50"
                          >
                            Use This Size
                          </button>
                          <button
                            onClick={reset}
                            className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 px-4 py-3 font-medium"
                          >
                            <RotateCcw className="w-4 h-4" />
                            Retake
                          </button>
                        </div>
                      </>
                    ) : (
                      <div>
                        <p className="text-gray-700 mb-1">
                          {result.notes || "We couldn't read a tyre size in that photo."}
                        </p>
                        <p className="text-sm text-gray-500 mb-4">
                          Try getting closer, using better light, and keeping the size text flat to the camera.
                        </p>
                        <button
                          onClick={reset}
                          className="inline-flex items-center gap-2 bg-blue-600 text-white px-5 py-3 rounded-xl font-semibold hover:bg-blue-700 transition-colors"
                        >
                          <RotateCcw className="w-4 h-4" />
                          Try Another Photo
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export default TyreSizeScanner
